import { prefix } from "../../../core/design-tokens/meta";
import {
    componentName,
    componentClasses,
    defaultProps,
    sizes,
    schema,
    views,
    eventMap,
} from "../../../core/components/popover/popover";
import {
    renderState,
    createStore,
    reducers,
    actions,
    dispatchEvent,
} from "../../../core/js/state-machine";
import {
    DOC,
    createElement,
    waitForRender,
    createCSSClass,
    getFocusableElements,
} from "../../../core/js/utilities";
import { closeX } from "../../../core/js/helpers/built-in-icons";
import validateInitialization from "../../../core/js/helpers/validate-initialization";
import { getPlacement, getPlacementFallback } from "../../../core/js/positioning/placement";
import { buildFocusTrap } from "../../../core/js/focus-trap/focus-trap";

/**
 * Validates Popover required elements
 * @param {HTMLElement} Popover
 * @returns {Boolean}
 */
export const validateHTML = (element, mergedProps) => {
    const triggerElement = DOC.querySelector(mergedProps.trigger);
    const bodyElement = element.querySelector(`.${componentClasses.body}`);
    const headerElement = element.querySelector(`.${componentClasses.header}`);
    const pointerElement = element.querySelector(`.${componentClasses.pointer}`);
    const closeElement = element.querySelector(`.${componentClasses.close}`);

    return triggerElement && bodyElement && headerElement
        ? { triggerElement, bodyElement, headerElement, pointerElement, closeElement }
        : false;
};

/**
 * Creates Popover
 * @param {HTMLElement} node
 * @returns Popover
 */
export default function Popover(element, props) {
    const [success, mergedProps, validElements] = validateInitialization({
        componentName,
        element,
        defaultProps,
        props,
        schema,
        validateHTML,
    });
    if (!success) return;

    const buildCloseElement = () => {
        return createElement("button", {
            class: `${componentClasses.close}`,
            html: closeX(),
            type: "button",
            "aria-label": mergedProps.srCloseLabel,
        });
    };

    const buildPointerElement = () => {
        return createElement("div", {
            class: `${componentClasses.pointer}`,
        });
    };

    /**
     * This method is being called to maintain proper size of the popover depending on the user screen size. If the provided size does not fit the
     * screen it will try to fit 1 step lower size until it reaches the small size which will be the last one to place on the screen.
     * @param {string} size the parameter of the popover size when the component is being initializated
     * @returns {string} size that will be rendered on the user screen.
     */

    const findValidSize = (size) => {
        const sizeArray = Object.keys(sizes);
        return window.innerWidth < sizes[size] + gutters.x * 2 &&
            sizeArray[sizeArray.indexOf(size) - 1]
            ? findValidSize(sizeArray[sizeArray.indexOf(size) - 1])
            : size;
    };

    // Method used to update the Popover when it is opening.
    const updatePopoverPosition = () => {
        const elementDynamicClassProperties = {};
        const pointerDynamicClassProperties = {};
        if (window.innerWidth <= sizes[mergedProps.size] + gutters.x * 2) {
            renderState(views.sizes[findValidSize(mergedProps.size)], element);
        } else {
            renderState(views.sizes[mergedProps.size], element);
        }

        const { position, pointer } = getPlacement({
            trigger: validElements.triggerElement,
            target: element,
            placement: mergedProps.placement,
            offsets: { x: pointerSize, y: pointerSize },
            gutters: { x: gutters.x, y: gutters.y },
            adjustToEdge: true,
            flip: true,
            pointer: pointerSize,
            placementFallback: getPlacementFallback(mergedProps.placement),
        });

        elementDynamicClassProperties.transform = `translate(${parseInt(
            position.left
        )}px, ${parseInt(position.top)}px)`;
        elementDynamicClass = `${prefix}dynamic-popover-t${parseInt(position.top)}-l${parseInt(
            position.left
        )}`;
        if (createCSSClass(`.${elementDynamicClass}`, elementDynamicClassProperties)) {
            renderState({ classList: [elementDynamicClass] }, element);
        }

        pointerDynamicClassProperties.left = `${pointer.left}px !important`;
        pointerDynamicClassProperties.top = `${pointer.top}px !important`;
        pointerDynamicClass = `${prefix}dynamic-popover__pointer-t${parseInt(
            pointer.top
        )}-l${parseInt(pointer.left)}`;
        if (createCSSClass(`.${pointerDynamicClass}`, pointerDynamicClassProperties)) {
            renderState({ classList: [pointerDynamicClass] }, validElements.pointerElement);
        }
    };

    // State event handler method
    const handleStateChange = (visible) => {
        if (visible) {
            renderState(views.beforeAnimation, element);
            updatePopoverPosition();
            waitForRender(element, "display", "block", 60).then(() => {
                renderState(views.open, element);
                const firstFocus = getFocusableElements(element)[1];
                firstFocus ? firstFocus.focus() : validElements.closeElement.focus();
                validElements.closeElement.addEventListener("click", toggle);
                DOC.addEventListener("keydown", handleKeydownEvent);
                window.addEventListener("resize", handleWindowResize);
                DOC.addEventListener("scroll", handleDocumentScroll, true);
                DOC.addEventListener("click", handleBlurEvent);
                renderState(views.afterAnimation, element);
            });
        } else {
            DOC.removeEventListener("click", handleBlurEvent);
            validElements.closeElement.removeEventListener("click", toggle);
            DOC.removeEventListener("keydown", handleKeydownEvent);
            window.removeEventListener("resize", handleWindowResize);
            DOC.removeEventListener("scroll", handleDocumentScroll, true);
            renderState(views.close, element);
            waitForRender(element, "opacity", "0", 60).then(() => {
                renderState(views.afterAnimation, element);
                // Clear all pointer dynamic classes
                renderState(
                    { classList: [`!${pointerDynamicClass}`] },
                    validElements.pointerElement
                );
                pointerDynamicClass = null;

                // Clear all element dynamic classes
                renderState(
                    { classList: [`!${elementDynamicClass}`, `!${elementDynamicSizeClass}`] },
                    element
                );
                elementDynamicClass = null;
                elementDynamicSizeClass = null;
                // If there was an original size put it back
                if (elementOriginalSizeClass) {
                    renderState({ classList: [elementOriginalSizeClass] }, element);
                }
            });
            dispatchEvent(visible ? eventMap.opened : eventMap.closed, element);
        }
    };

    // Event handling methods
    const handleWindowResize = () => {
        if (visibilityStore.getState()) visibilityStore.dispatch(actions.close());
    };

    const handleDocumentScroll = (e) => {
        if (
            validElements.triggerElement.offsetParent !== document.body &&
            e.target !== validElements.bodyElement
        )
            visibilityStore.dispatch(actions.close());
    };

    const handleUserIsTabbing = () => {
        if (document.body.classList.contains("user-is-tabbing"))
            validElements.triggerElement.focus();
    };

    const handleBlurEvent = (e) => {
        if (
            visibilityStore.getState() &&
            element !== e.target &&
            element.contains(e.target) !== true &&
            validElements.triggerElement !== e.target &&
            !validElements.triggerElement.contains(e.target)
        ) {
            visibilityStore.dispatch(actions.close());
        }
    };

    const handleKeydownEvent = (e) => {
        if (visibilityStore.getState()) {
            switch (e.keyCode) {
                case 27: // Esc
                    visibilityStore.dispatch(actions.close());
                    handleUserIsTabbing();
                    break;
            }
        }
    };

    // Api methods
    const dispose = () => {
        focusTrap.dispose();
        if (visibilityStore.getState()) {
            renderState(views.close, element);
            renderState(views.afterAnimation, element);
            // Clear all pointer dynamic classes
            renderState({ classList: [`!${pointerDynamicClass}`] }, validElements.pointerElement);
            pointerDynamicClass = null;

            // Clear all element dynamic classes
            renderState(
                { classList: [`!${elementDynamicClass}`, `!${elementDynamicSizeClass}`] },
                element
            );
            elementDynamicClass = null;
            elementDynamicSizeClass = null;
            // If there was an original size put it back
            if (elementOriginalSizeClass) {
                renderState({ classList: [elementOriginalSizeClass] }, element);
            }
        }
        window.removeEventListener("resize", handleWindowResize);
        DOC.removeEventListener("scroll", handleDocumentScroll, true);
        DOC.removeEventListener("click", handleBlurEvent);
        DOC.removeEventListener("keydown", handleKeydownEvent);
        validElements.closeElement.remove();
        validElements.pointerElement.remove();
        element[componentName] = null;
        element = null;
        validElements.triggerElement.removeEventListener("click", toggle);
    };

    const open = () => {
        visibilityStore.dispatch(actions.open());
    };

    const close = () => {
        visibilityStore.dispatch(actions.close());
    };

    const toggle = () => {
        if (visibilityStore.getState()) {
            visibilityStore.dispatch(actions.close());
            handleUserIsTabbing();
        } else {
            visibilityStore.dispatch(actions.open());
        }
    };

    const getVisibility = () => {
        return visibilityStore.getState();
    };

    // Injected HTML elements
    const injectPointerElement = () => {
        validElements.pointerElement = buildPointerElement();
        element.insertBefore(validElements.pointerElement, element.firstChild);
    };

    const injectCloseElement = () => {
        validElements.closeElement = buildCloseElement();
        validElements.headerElement.appendChild(validElements.closeElement);
    };

    // Variables
    // the original size is used to reset the size. when we open the popover and the window
    // is smaller than the original size we replace it. this size gets put back when closing.
    const originalSize = Array.from(element.classList).filter((clazz) => {
        return /(--)(sm|md|lg)/.test(clazz);
    });

    const elementOriginalSizeClass = originalSize[0];
    let elementDynamicSizeClass, pointerDynamicClass, elementDynamicClass;

    // Build HTML
    if (!validElements.pointerElement) injectPointerElement();
    if (!validElements.closeElement) injectCloseElement();

    // Create the Focus Trap
    const focusTrap = buildFocusTrap(element);
    const gutters = { x: 16, y: 16 };
    const pointerSize = 12;

    // Add events
    validElements.triggerElement.addEventListener("click", toggle);

    // Create store
    const visibilityStore = createStore(reducers.boolReducer, false);
    visibilityStore.subscribe(handleStateChange);

    // Api
    const api = {
        dispose,
        open,
        close,
        toggle,
        getVisibility,
    };

    // Assignment
    element[componentName] = api;

    return api;
}
